import { useEffect } from "react";
import { styled } from "styled-components";

import { ExclamationCircleIcon } from "@vericus/cadmus-icons";
import { Button, colors, Desk, LinkButton, Text } from "@vericus/cadmus-ui";

import { hide, register, show } from "@ebay/nice-modal-react";

import { __GLOBAL_TENANT } from "@/client/globals";
import { useAppSelector } from "@/data/hooks";
import {
  selectHasRetryableSaveError,
  selectIsConnected,
  selectIsDocumentError,
  selectSaveError,
  selectSubmitNetworkError,
} from "@/features/authority";
import { useRespondusLDB } from "@/hooks/use-respondus-ldb";

import { LMSReturnLink } from "../lms-return-link";
import { ConnectionErrorModal } from "./ConnectionErrorModal";
import { useRetrySaveCallback } from "./use-save-retry-callback";

const ERROR_MODAL_ID = "ConnectionErrorModal";
register(ERROR_MODAL_ID, ConnectionErrorModal);

/**
 * Top Status Bar showing networking connection status messages.
 */
export function ConnectionBar() {
  const saveError = useAppSelector(selectSaveError);
  const submitError = useAppSelector(selectSubmitNetworkError);
  const connected = useAppSelector(selectIsConnected);
  const docError = useAppSelector(selectIsDocumentError);
  const { isLDBExam } = useRespondusLDB();
  const isSupervisedLockDownExam = isLDBExam && tenantWantsSupervisedLDB();

  const hasError = !connected || !!saveError || !!submitError || docError;
  const hasRetryableSaveError = useAppSelector(selectHasRetryableSaveError);

  // Callback to request a new save
  const [onRetrySave, _] = useRetrySaveCallback();

  // Show the the lockdown exam disconnection modal that prompts the user to
  // make a conscious decision to continue offline
  useEffect(() => {
    if (hasRetryableSaveError && isSupervisedLockDownExam) {
      show(ERROR_MODAL_ID);
    }
    return () => {
      hide(ERROR_MODAL_ID);
    };
  }, [hasRetryableSaveError, isSupervisedLockDownExam]);

  if (!hasError) {
    return null;
  }

  return (
    <InnerConnectionBar
      unauthorizedError={!!saveError && saveError.kind === "unauthorized"}
      connectionError={!connected}
      saveNetworkError={!!saveError && saveError.kind === "network"}
      saveRejectedError={!!saveError && saveError.kind === "rejected"}
      submitError={!!submitError}
      documentError={!!docError}
      isSupervisedLockDownExam={isSupervisedLockDownExam}
      onRetrySave={onRetrySave}
    />
  );
}

interface InnerProps {
  unauthorizedError?: boolean;
  connectionError: boolean;
  saveNetworkError: boolean;
  saveRejectedError: boolean;
  submitError: boolean;
  documentError: boolean;
  isSupervisedLockDownExam: boolean;
  onRetrySave?: () => void;
}

type Severity = "failure" | "error";

export function InnerConnectionBar(props: InnerProps) {
  const {
    unauthorizedError,
    connectionError,
    saveNetworkError,
    saveRejectedError,
    submitError,
    documentError,
    isSupervisedLockDownExam,
    onRetrySave,
  } = props;

  let title;
  let message;
  let action;
  let severity: Severity = "error";

  if (documentError) {
    title = "Document Error";
    message =
      "The current document has encountered an error. Please reload the page to restore the document.";
    severity = "failure";
    action = (
      <Button
        size="sm"
        kind="secondary"
        onClick={() => window.location.reload()}
        marginLeft={8}
      >
        Reload document
      </Button>
    );
  }

  if (unauthorizedError) {
    title = "Expired Session";
    message = "Your session has expired or you have been signed out.";
    severity = "failure";
    action = <LMSReturnLink label={"Please log in again to continue"} />;
  }

  if (connectionError) {
    title = "Working Offline";
    message =
      "Do not refresh — your progress will be automatically saved once you’re reconnected to the internet.";
  }

  if (saveNetworkError || submitError) {
    title = "Working Offline";
    message =
      "Please do not refresh — progress will be automatically saved once you’re reconnected.";
  }

  // Distinct from the offline states above: the server was reached and refused
  // the save, so promising it will save on reconnect would be untrue.
  if (saveRejectedError) {
    title = "Not Saving";
    message =
      "There's an error saving your recent changes to the server. Please take a copy of your work and refresh the page.";
  }

  if (
    isSupervisedLockDownExam &&
    (connectionError || saveNetworkError || saveRejectedError || submitError)
  ) {
    title = "No internet connection, please notify your supervisor";
    message =
      "Do not refresh to avoid losing your progress. Attempting to reconnect...";
    severity = "failure";
  }

  if ((saveNetworkError || saveRejectedError) && onRetrySave) {
    action = (
      <LinkButton
        inline
        onClick={onRetrySave}
        color="orange800"
        title="Retry saving"
      >
        {saveRejectedError ? "Try again" : "Reconnect now"}
      </LinkButton>
    );
  }

  return (
    <Container role="status" $failure={severity === "failure"}>
      <ExclamationCircleIcon
        size={20}
        label=""
        style={{
          flex: "none",
          color: severity === "failure" ? colors.failure : colors.orangeA500,
        }}
      />
      <Text kind="bodyMd" color="inherit" as={"div"}>
        <strong>{title}</strong>
        {" ∙ "} {message} {action}
      </Text>
    </Container>
  );
}

function tenantWantsSupervisedLDB() {
  const tenant = __GLOBAL_TENANT.current;
  if (tenant && tenant.startsWith("uom")) return true;
  if (tenant && tenant.startsWith("cadmus")) return true;
  return false;
}

const Container = styled(Desk.Shelf)<{ $failure?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 12px;
  min-height: 54px;
  box-sizing: border-box;
  position: relative;
  z-index: 10;
  background: ${(p) => (p.$failure ? colors.failureBg : colors.orange200)};
  color: ${(p) => (p.$failure ? colors.black100 : colors.orange800)};
  border-color: ${(p) => (p.$failure ? colors.failure : colors.orangeA500)};
`;
